import React from "react";

/**
 * Reusable SubTask form component
 * Used in both Registry and Project contexts
 */
const SubTaskForm = ({
  value,
  onChange,
  mode = "project",
  showSaveToRegistry = false,
  disabled = false,
  defaultBillingModel = ""
}) => {
  const handleChange = (field, val) => {
    onChange({ ...value, [field]: val });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium mb-1">Name *</label>
        <input
          type="text"
          value={value.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          placeholder="e.g., Site Survey"
          disabled={disabled}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          value={value.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          className="w-full px-3 py-2 border rounded"
          rows={2}
          placeholder="Optional description..."
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Weight</label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={value.weight || ""}
            onChange={(e) => handleChange("weight", e.target.value ? parseFloat(e.target.value) : "")}
            className="w-full px-3 py-2 border rounded"
            placeholder="1.0"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Est. Hours</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={value.estimatedHours || ""}
            onChange={(e) => handleChange("estimatedHours", e.target.value ? parseFloat(e.target.value) : "")}
            className="w-full px-3 py-2 border rounded"
            placeholder="4.0"
            disabled={disabled}
          />
        </div>
      </div>

      {mode === "project" && (
        <div className="border-t pt-3">
          <label className="block text-sm font-medium mb-1">Billing Model</label>
          <select
            value={value.billingModel || ""}
            onChange={(e) => handleChange("billingModel", e.target.value)}
            className="w-full px-3 py-2 border rounded"
            disabled={disabled}
          >
            <option value="">Use parent task billing ({defaultBillingModel || "not set"})</option>
            <option value="hourly">Hourly/Ongoing</option>
            <option value="fixed_price">Fixed Price</option>
          </select>

          {(value.billingModel || defaultBillingModel) === "hourly" && (
            <div className="mt-3">
              <label className="block text-sm font-medium mb-1">Hourly Rate (kr/hr)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={value.hourlyRate || ""}
                onChange={(e) => handleChange("hourlyRate", e.target.value)}
                className="w-full px-3 py-2 border rounded"
                placeholder="e.g., 850"
                disabled={disabled}
              />
            </div>
          )}

          {(value.billingModel || defaultBillingModel) === "fixed_price" && (
            <div className="mt-3">
              <label className="block text-sm font-medium mb-1">Fixed Price (kr)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={value.fixedPrice || ""}
                onChange={(e) => handleChange("fixedPrice", e.target.value)}
                className="w-full px-3 py-2 border rounded"
                placeholder="e.g., 150000"
                disabled={disabled}
              />
            </div>
          )}
        </div>
      )}

      <div className="border-t pt-3">
        <label className="flex items-start">
          <input
            type="checkbox"
            checked={value.showDescriptionInTimeReport || false}
            onChange={(e) => handleChange("showDescriptionInTimeReport", e.target.checked)}
            className="mt-1 mr-2"
            disabled={disabled}
          />
          <div>
            <div className="font-medium text-sm">Show description field in time reports</div>
            <div className="text-xs text-gray-600">
              When enabled, workers can add a description when logging time for this subtask
            </div>
          </div>
        </label>
        {value.showDescriptionInTimeReport && (
          <label className="flex items-start mt-3 ml-6">
            <input
              type="checkbox"
              checked={value.requireDescriptionInTimeReport || false}
              onChange={(e) => handleChange("requireDescriptionInTimeReport", e.target.checked)}
              className="mt-1 mr-2"
              disabled={disabled}
            />
            <div>
              <div className="font-medium text-sm">Require description in time reports</div>
              <div className="text-xs text-gray-600">
                When enabled, workers must enter a description when logging time for this subtask
              </div>
            </div>
          </label>
        )}
      </div>

      {showSaveToRegistry && mode === "project" && (
        <div className="border-t pt-3">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={value.saveToRegistry || false}
              onChange={(e) => handleChange("saveToRegistry", e.target.checked)}
              className="mt-1 mr-2"
              disabled={disabled}
            />
            <div>
              <div className="font-medium text-sm">Save to Registry</div>
              <div className="text-xs text-gray-600">
                Make this subtask reusable for future projects
              </div>
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default SubTaskForm;
